import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useOrganizationService } from 'hooks/organization';
import { useAudienceService } from 'hooks/users/audience';
import { useContactService } from 'hooks/users/contact';
import { useCampaignService } from 'hooks/mailing/campaign';
import { useSenderIDService } from 'hooks/messaging/sender-id';
import { useWebPushService } from 'hooks/messaging/push/web';
import { useTransactionalService } from 'hooks/mailing/transactional';
import { useKeyService } from 'hooks/key';
import { useMailingConfigService } from './mailing/configuration';
import { useMailLogService } from './maillog';

const DEFAULT_STEPS = [
  { id: 'create_audience', section: null },
  { id: 'import_contacts', section: null },
  { id: 'setup_dns', section: null },
  { id: 'send_campaign', section: null }
];

export function generateSteps(qualification) {
  const { goal, channels = [] } = qualification;

  if (goal === 'marketing') {
    const ids = ['create_audience', 'import_contacts'];
    if (channels.includes('sms')) ids.push('request_sms_sender');
    if (channels.includes('push')) ids.push('install_push_sdk');
    ids.push('setup_dns', 'send_campaign');
    return ids.map((id) => ({ id, section: null }));
  }

  if (goal === 'transactional') {
    return [
      'create_transactional_template',
      'setup_dns',
      'generate_api_key',
      'send_test_transactional',
      'enable_retry_on_failure'
    ].map((id) => ({ id, section: null }));
  }

  if (goal === 'both') {
    return [
      { id: 'create_audience', section: 'campaign' },
      { id: 'import_contacts', section: 'campaign' },
      { id: 'setup_dns', section: 'campaign' },
      { id: 'send_campaign', section: 'campaign' },
      { id: 'create_transactional_template', section: 'transactional' },
      { id: 'generate_api_key', section: 'transactional' },
      { id: 'send_test_transactional', section: 'transactional' }
    ];
  }

  return [];
}

export function getHeaderMessage(qualification) {
  if (!qualification) return null;
  const { goal, role, timeline, transactional_setup } = qualification;
  if (goal === 'transactional') {
    return transactional_setup === 'api_team'
      ? "Let's get your team set up."
      : 'Your integration starts here.';
  }
  if (role === 'agency') return "Let's set up your first client.";
  const by_timeline = {
    this_week: "Let's get you sending this week.",
    this_month: "You're building something solid.",
    exploring: "Take your time. We'll be here."
  };
  return by_timeline[timeline] || "Let's get started.";
}

function computeStepStates(steps, completion) {
  let found_active = false;
  return steps.map((step) => {
    const is_complete = completion[step.id] === true;
    let state;
    if (is_complete) {
      state = 'complete';
    } else if (!found_active) {
      state = 'active';
      found_active = true;
    } else {
      state = 'pending';
    }
    return { ...step, state };
  });
}

export const useChecklist = () => {
  const { tenant_id } = useSelector((store) => store.user_data);

  const { fetchOrganizations } = useOrganizationService();
  const { fetchAudiences } = useAudienceService();
  const { fetchContacts } = useContactService();
  const { fetchCampaigns } = useCampaignService();
  const { fetchSenderIds } = useSenderIDService();
  const { fetchPushMessages } = useWebPushService();
  const { fetchTransactionals } = useTransactionalService();
  const { fetchMailLogs } = useMailLogService();
  const { fetchKey } = useKeyService();
  const { fetchConfiguration } = useMailingConfigService();

  const [org, setOrg] = useState(null);
  const [completion, setCompletion] = useState({});
  const [loading, setLoading] = useState(true);

  const checkCompletion = async (step_ids) => {
    const needs = new Set(step_ids);
    const entries = [];

    if (needs.has('create_audience'))
      entries.push([
        'create_audience',
        fetchAudiences({ query_string: 'population=1' })
          .then(({ size }) => size > 0)
          .catch(() => false)
      ]);

    if (needs.has('import_contacts'))
      entries.push([
        'import_contacts',
        fetchContacts({ query_string: 'population=1' })
          .then(({ size }) => size > 0)
          .catch(() => false)
      ]);

    if (needs.has('setup_dns'))
      entries.push([
        'setup_dns',
        fetchConfiguration()
          .then(({ configurations: [configuration] }) => {
            const { dns } = configuration || {};
            return dns && dns.length > 1;
          })
          .catch(() => false)
      ]);

    if (needs.has('send_campaign'))
      entries.push([
        'send_campaign',
        fetchCampaigns({ query_string: 'status=processed&population=1&bool=-is_test_campaign' })
          .then(({ size }) => size > 0)
          .catch(() => false)
      ]);

    if (needs.has('request_sms_sender'))
      entries.push([
        'request_sms_sender',
        fetchSenderIds({ query_string: 'population=1' })
          .then(({ size }) => size > 0)
          .catch(() => false)
      ]);

    if (needs.has('install_push_sdk'))
      entries.push([
        'install_push_sdk',
        fetchPushMessages({ query_string: 'population=1' })
          .then(({ size }) => size > 0)
          .catch(() => false)
      ]);

    if (needs.has('create_transactional_template'))
      entries.push([
        'create_transactional_template',
        fetchTransactionals({ query_string: 'population=1' })
          .then(({ size }) => size > 0)
          .catch(() => false)
      ]);

    if (needs.has('generate_api_key'))
      entries.push([
        'generate_api_key',
        fetchKey(tenant_id)
          .then((key) => !!key)
          .catch(() => false)
      ]);

    if (needs.has('send_test_transactional'))
      entries.push([
        'send_test_transactional',
        fetchMailLogs({ query_string: 'class=transactional&population=1' })
          .then(({ size }) => size > 0)
          .catch(() => false)
      ]);

    if (needs.has('enable_retry_on_failure'))
      entries.push([
        'enable_retry_on_failure',
        fetchTransactionals({ query_string: 'bool=retry_email&population=1' })
          .then(({ size }) => size > 0)
          .catch(() => false)
      ]);

    const results = await Promise.all(entries.map(([, p]) => p));
    const new_completion = {};
    entries.forEach(([id], i) => {
      new_completion[id] = results[i];
    });
    setCompletion(new_completion);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const { orgs } = await fetchOrganizations({ query_string: `id=${tenant_id}` });
      const org_data = orgs?.[0] || null;
      setOrg(org_data);
      const steps = org_data?.qualification?.completed_at
        ? generateSteps(org_data.qualification)
        : DEFAULT_STEPS;
      await checkCompletion(steps.map((s) => s.id));
    } finally {
      setLoading(false);
    }
  };

  const qualification = org?.qualification || null;
  const all_steps = qualification?.completed_at ? generateSteps(qualification) : DEFAULT_STEPS;
  const steps_with_state = computeStepStates(all_steps, completion);

  const is_both = qualification?.goal === 'both';
  let current_phase = null;
  let active_steps = steps_with_state;

  if (is_both) {
    const campaign_ids = new Set([
      'create_audience',
      'import_contacts',
      'setup_dns',
      'send_campaign'
    ]);
    const campaign_done = steps_with_state
      .filter((s) => campaign_ids.has(s.id))
      .every((s) => s.state === 'complete');
    current_phase = campaign_done ? 'transactional' : 'campaign';
    active_steps = steps_with_state.filter((s) => s.section === current_phase);
  }

  const completed_count = active_steps.filter((s) => s.state === 'complete').length;
  const total_count = active_steps.length;
  const progress_pct = total_count ? Math.round((completed_count / total_count) * 100) : 0;

  const show_banner = !!(org && qualification?.completed_at && !org.custom_domain_configured);
  const phase_complete =
    active_steps.length > 0 && active_steps.every((s) => s.state === 'complete');
  const has_existing_data = current_phase !== 'transactional' && completion.send_campaign === true;
  const show_checklist = !!(org && !phase_complete && !has_existing_data);
  const header_message = org?.checklist_completed
    ? "You're all set. Let's grow."
    : current_phase === 'transactional'
    ? 'Campaigns are live. Now set up your transactional emails.'
    : getHeaderMessage(qualification) || "Let's get started.";

  return {
    org,
    qualification,
    steps: active_steps,
    current_phase,
    completed_count,
    total_count,
    progress_pct,
    header_message,
    show_banner,
    show_checklist,
    loading,
    refresh
  };
};
